import React, { useEffect, useRef, useState, type ReactNode, type HTMLAttributes } from "react";
import Box from "@mui/material/Box";
import { useForkRef } from "@mui/material/utils";
import ToolTipComponent from "../tooltip";
import { PRIMARY_INPUT_CAN_HOVER } from "../../utils/touchInput";
import { untranslated, type TranslatedString } from "@i18n";
import type { SxProps, Theme } from "@mui/material/styles";
import type { TooltipPosition } from "../../interface";

interface TextProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  /** The text's own box. A tooltip wrapped around a Text anchors to it through this. */
  ref?: React.Ref<HTMLDivElement> | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
  /** Tooltip content - strings must be wrapped in t() for internationalization */
  tooltipContent?: TranslatedString | React.ReactElement | undefined;
  alwaysShowTooltip?: boolean | undefined;
  sx?: SxProps<Theme> | undefined;
  placement?: TooltipPosition | undefined;
  grid?: boolean | undefined;
  noWrap?: boolean | undefined;
  hideToolTip?: boolean | undefined;
}

// `as const` so the keyword values keep their literal types: `whiteSpace` and `WebkitBoxOrient` are
// keyword-only in csstype, and a widened `string` is not one of the values MUI's `sx` accepts.
const sxMapper = {
  grid: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
  },
  normal: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "nowrap",
  },
  noWrap: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "normal",
  },
} as const;

/** One entry of MUI's array form of `sx`, read off `SxProps<Theme>` itself so it cannot drift. */
type SxEntry = Extract<SxProps<Theme>, readonly unknown[]>[number];

/**
 * `sx` arrives as a style object, a theme callback, an array of either, or nothing, and the merge
 * below splices it into MUI's array form. `Array.isArray` cannot make that split without erasing
 * the entries — its lib signature is `arg is any[]` while MUI declares the array form
 * `ReadonlyArray<…>`, which is not assignable to `any[]` — so the array form is recognised here
 * instead. An absent `sx` contributes nothing, exactly as the `undefined` entry the previous
 * `[sx]` spliced in did.
 */
const isSxArray = (value: SxProps<Theme>): value is readonly SxEntry[] => Array.isArray(value);
const sxEntries = (value: SxProps<Theme> | undefined): readonly SxEntry[] => {
  if (value === undefined) return [];
  return isSxArray(value) ? value : [value];
};

const Text: React.FC<TextProps> = ({
  children,
  className,
  tooltipContent,
  alwaysShowTooltip = false,
  style = {},
  sx,
  placement,
  grid = false,
  noWrap = false,
  hideToolTip = false,
  ref,
  ...rest
}) => {
  // Set while the tooltip is open: what it shows, read off the box when it opened.
  const [tooltipTitle, setTooltipTitle] = useState<TranslatedString | React.ReactElement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const forkedRef = useForkRef(ref, boxRef);
  const open = tooltipTitle !== null;

  useEffect(() => {
    if (!open) return;
    const hideTooltip = () => setTooltipTitle(null);
    window.addEventListener("scroll", hideTooltip, true);
    return () => window.removeEventListener("scroll", hideTooltip, true);
  }, [open]);

  // `sx` is also allowed to be a theme callback or an array of style objects; only the plain
  // style-object form can declare a `whiteSpace` up front, which is all this check ever read.
  const sxWhiteSpace = sx && typeof sx === "object" && "whiteSpace" in sx ? sx.whiteSpace : undefined;
  // Whether this Text ever shows a tooltip: always, or text it cuts off (wrapping text is never cut off). One that
  // never does leaves its tooltip empty and uncontrolled, so no tap is taken for it.
  const canShow = alwaysShowTooltip || (!hideToolTip && (sxWhiteSpace != "normal" || noWrap));

  // MUI calls this once a hover has lasted its enter delay or on keyboard focus, and the shared tooltip on a tap
  // where the primary input cannot hover. A hover's event no longer has a currentTarget by then, so the box is
  // read through the ref. Opens only when there is something to show.
  const openIfShown = () => {
    const el = boxRef.current;
    // Unmounted between the open request and this call: nothing left to measure.
    if (!el) return;
    // 1px tolerance: rem sizes under the viewport-clamped root font are fractional, and the rounded scroll/client
    // sizes of a label that exactly fits can differ by a pixel, which showed a tooltip on fully visible text
    const hasOverflow = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
    if (alwaysShowTooltip || (canShow && hasOverflow)) {
      // The fallback tooltip is the text this element shows (its DOM text), whatever nodes `children` are: an
      // element child (`<Text><Trans …/></Text>`) reads as its text, and array children as they render, not
      // comma-joined. It is shown text, already translated or data, so it takes the brand for dynamic content.
      setTooltipTitle(tooltipContent || untranslated(el.textContent) || null);
    }
  };

  return (
    <ToolTipComponent
      content={tooltipTitle}
      {...(canShow && { open, onOpen: openIfShown, onClose: () => setTooltipTitle(null) })}
      // With a mouse it closes once the pointer leaves the text, not when it reaches the tooltip, which would cover
      // what is under it. A tooltip a tap opens takes its own taps.
      disableInteractive={PRIMARY_INPUT_CAN_HOVER}
      position={placement ?? "top"}
    >
      <Box
        ref={forkedRef}
        component="div"
        className={className}
        // MUI's documented merge for a wrapper that owns base styles and forwards `sx`. Spreading
        // `sx` into one object only ever worked for its plain-object form - the callback and array
        // forms have no own style keys - which is what the erased assertion was hiding.
        sx={[
          {
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
            fontFamily: "inherit",
            ...sxMapper[grid ? "grid" : noWrap ? "noWrap" : "normal"],
          },
          ...sxEntries(sx),
          style,
        ]}
        // The caller's own handlers, an outer tooltip's among them (MUI's Tooltip clones a `<Text>` it wraps with
        // its open and close handlers), reach the box as they are.
        {...rest}
      >
        {children}
      </Box>
    </ToolTipComponent>
  );
};

export default Text;
